import { html } from "@codemirror/lang-html";
import Avatar from "@mui/material/Avatar";
import Button from "@mui/material/Button";
import Card from "@mui/material/Card";
import CardHeader from "@mui/material/CardHeader";
import { ArrowSquareOut as ArrowSquareOutIcon } from "@phosphor-icons/react/ArrowSquareOut";
import { Plus as PlusIcon } from "@phosphor-icons/react/Plus";
import { UserPlus as UserPlusIcon } from "@phosphor-icons/react/UserPlus";
import CodeMirror from "@uiw/react-codemirror";
import { paths } from "config/paths";
import RouterLink from "next/link";
import { useCopyToClipboard } from "usehooks-ts";
import { DashboardContentLayout } from "~ui-components/components/atoms/dashboard-content-layout";
import { useWebRouter } from "~utils/next-router/use-web-router";

import Box from "@mui/material/Box";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import FormControl from "@mui/material/FormControl";
import FormControlLabel from "@mui/material/FormControlLabel";
import IconButton from "@mui/material/IconButton";
import InputAdornment from "@mui/material/InputAdornment";
import InputLabel from "@mui/material/InputLabel";
import OutlinedInput from "@mui/material/OutlinedInput";
import Radio from "@mui/material/Radio";
import RadioGroup from "@mui/material/RadioGroup";
import Stack from "@mui/material/Stack";
import { Copy } from "@phosphor-icons/react/Copy";
import { X } from "@phosphor-icons/react/dist/icons/X";
import { toast } from "~ui-components/components/atoms/toaster";
import { useDialog } from "~ui-components/hooks/use-dialog";
import { UserGroup } from "~ui-components/types/__generated/gql/graphql";
import {
	MAP_ACCESS_HIDDEN,
	MAP_EMBED_LOAD_CURRENT,
	MAP_EMBED_LOAD_DEFAULT,
	MapAccessForm,
	MapAccessFormProps,
} from "./map-access-form";

interface MapAccessProps {
	form: MapAccessFormProps["form"];
	groups: UserGroup[];
	isGroupsLoading: boolean;
	isManageGroupAllowed: boolean;
}

export function MapAccess(props: MapAccessProps) {
	return (
		<DashboardContentLayout
			pageTitle="Access"
			action={<AccessTopAction {...props} />}>
			<Card>
				<CardHeader
					action={
						<Button
							color="secondary"
							endIcon={<ArrowSquareOutIcon />}>
							Learn more
						</Button>
					}
					avatar={
						<Avatar>
							<UserPlusIcon fontSize="var(--Icon-fontSize)" />
						</Avatar>
					}
					title="Map access"
					subheader="Choose who can view or modify this map"
				/>
				<MapAccessForm {...props} />
			</Card>
		</DashboardContentLayout>
	);
}

const AccessTopAction = ({ form, isManageGroupAllowed }: MapAccessProps) => {
	const router = useWebRouter();
	const [, copyToClipboard] = useCopyToClipboard();
	const {
		open: isEmbedOpen,
		handleClose: closeEmbed,
		handleOpen: openEmbed,
	} = useDialog();

	const link = `https://${process.env.ROUTER_DOMAIN_NAME}${paths.accountName.maps.mapId.mapName.root(
		router._query.accountName,
		form.values.isPublic &&
			form.values.publicAccessConfigs?.type === MAP_ACCESS_HIDDEN
			? form.values.publicAccessConfigs?.hiddenId
			: router._query.mapId,
		router._query.mapName,
	)}`;

	return (
		<Stack
			direction="row"
			gap={2}>
			{isEmbedOpen && (
				<EmbedDialog
					form={form}
					onClose={closeEmbed}
				/>
			)}

			<OutlinedInput
				sx={{
					width: "500px",
				}}
				endAdornment={
					<InputAdornment position="end">
						<IconButton
							onClick={() =>
								toast.promise(copyToClipboard(link), {
									success: "Link copied to clipboard",
									error: "Something went wrong!",
								})
							}>
							<Copy />
						</IconButton>
					</InputAdornment>
				}
				value={link}
			/>
			<Button
				variant="outlined"
				onClick={() => {
					openEmbed();
				}}>
				Embed
			</Button>
			{isManageGroupAllowed && (
				<div style={{ textWrap: "nowrap" }}>
					<Button
						component={RouterLink}
						startIcon={<PlusIcon />}
						variant="contained"
						href={paths.accountName.groups.root(router._query.accountName)}>
						Add or manage group
					</Button>
				</div>
			)}
		</Stack>
	);
};

const EmbedDialog = ({
	form,
	onClose,
}: Pick<MapAccessProps, "form"> & { onClose: () => void }) => {
	const router = useWebRouter();
	return (
		<Dialog
			open
			fullWidth
			maxWidth="sm">
			<DialogTitle>
				<Stack
					direction="row"
					alignItems="center">
					Embed map
					<Box style={{ flex: 1 }} />
					<IconButton onClick={onClose}>
						<X />
					</IconButton>
				</Stack>
			</DialogTitle>
			<DialogContent>
				<Stack gap={2}>
					<FormControl>
						<InputLabel>Copy embed code</InputLabel>
						<Box
							sx={{
								".cm-content": {
									width: "100%",
									textWrap: "wrap",
								},
							}}>
							<CodeMirror
								value={`<iframe style="display: none;" src="${`https://${process.env.ROUTER_DOMAIN_NAME}${paths.accountName.maps.mapId.mapName.root(
									router._query.accountName,
									router._query.mapId,
									router._query.mapName,
								)}`}?preview=true" allowtransparency="true" frameborder="0" scrolling="no" allowfullscreen="" mozallowfullscreen="" webkitallowfullscreen="" oallowfullscreen="" msallowfullscreen="" width="800" height="600" onload="this.style.display='block';"></iframe>`}
								height="200px"
								extensions={[html()]}
								theme="dark"
								editable={false}
							/>
						</Box>
					</FormControl>

					<RadioGroup {...form.getFieldProps("embedConfigs.loadType")}>
						<FormControlLabel
							value={MAP_EMBED_LOAD_DEFAULT}
							control={<Radio />}
							label="Load with default zoom level, map position, and visible groups"
						/>
						<FormControlLabel
							value={MAP_EMBED_LOAD_CURRENT}
							control={<Radio />}
							label="Load with current zoom level, map position, and visible groups"
						/>
					</RadioGroup>
				</Stack>
			</DialogContent>
		</Dialog>
	);
};

export * from "./map-access-form";
